import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import type BankTransferDetailViewModel from "viewModels/finance/outgoingPayments/bankTransferDetailViewModel";
import { observer } from "mobx-react-lite";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import type TransferType from "entities/transferType";
import { formatAmount, formatDate } from "utils/localizations";
import Section from "views/common/section";
import { emptyValue } from "utils/emptyValue";

const OutgoingBankTransferPaymentSection: ViewComponent<BankTransferDetailViewModel> = observer(({ vm }) => {
  return (
    <Section title={vm.translate("outgoingPaymentTitle")} context={vm.item}>
      <div className="row">
        <Form.Group as={Col}>
          <Form.Label>{vm.translateOutgoingPaymentAttribute("recipientBankAccount")}</Form.Label>
          <FakeInput
            target={vm.item.outgoingPayment}
            property="recipientBankAccount"
            valueFormatter={value => formatBankAccount(value)}
          />
        </Form.Group>
      </div>
      <div className="row">
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translateOutgoingPaymentAttribute("variableSymbol")}</Form.Label>
          <FakeInput target={vm.item.outgoingPayment} property="variableSymbol" />
        </Form.Group>
        {vm.showSettledFields && (
          <>
            <Form.Group as={Col} sm={3}>
              <Form.Label>{vm.translateOutgoingPaymentAttribute("constantSymbol")}</Form.Label>
              <FakeInput target={vm.item.outgoingPayment} property="constantSymbol" />
            </Form.Group>
            <Form.Group as={Col} sm={3}>
              <Form.Label>{vm.translateOutgoingPaymentAttribute("specificSymbol")}</Form.Label>
              <FakeInput target={vm.item.outgoingPayment} property="specificSymbol" />
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{vm.translateOutgoingPaymentAttribute("paymentType")}</Form.Label>
              <FakeInput
                target={vm.item.outgoingPayment}
                property="paymentType"
                valueFormatter={(paymentType: TransferType) => vm.transferPaymentTypeName(paymentType)}
              />
            </Form.Group>
          </>
        )}
        <Form.Group as={Col} sm={vm.showSettledFields ? 3 : 6}>
          <Form.Label>{vm.translateOutgoingPaymentAttribute("paidAmount")}</Form.Label>
          <FakeInput target={vm.item.outgoingPayment} property="paidAmount" valueFormatter={value => formatAmount(value)} />
        </Form.Group>
        {vm.showSettledFields && (
          <Form.Group as={Col} sm={3}>
            <Form.Label>{vm.translateOutgoingPaymentAttribute("postingDate")}</Form.Label>
            <FakeInput
              target={vm.item.outgoingPayment}
              property="postingDate"
              valueFormatter={value => formatDate(value) || emptyValue}
            />
          </Form.Group>
        )}
      </div>
      {vm.showSettledFields && (
        <div className="row">
          <Form.Group as={Col} sm={12}>
            <Form.Label>{vm.translateOutgoingPaymentAttribute("messageForRecipient")}</Form.Label>
            <FakeInput target={vm.item.outgoingPayment} property="messageForRecipient" />
          </Form.Group>
          <Form.Group as={Col} sm={12}>
            <Form.Label>{vm.translateOutgoingPaymentAttribute("senderBankAccount")}</Form.Label>
            <FakeInput
              target={vm.item.outgoingPayment}
              property="senderBankAccount"
              valueFormatter={value => (value ? value.accountNumber : emptyValue)}
            />
          </Form.Group>
        </div>
      )}
      <div className="row">
        <Form.Group as={Col} sm={12}>
          <Form.Label>{vm.translateOutgoingPaymentAttribute("status")}</Form.Label>
          <FakeInput
            target={vm.item.outgoingPayment}
            property="status"
            valueFormatter={value => (value ? vm.translateOutgoingTransferPaymentStatus(value) : emptyValue)}
          />
        </Form.Group>
      </div>
    </Section>
  );
});

export default OutgoingBankTransferPaymentSection;
